import { useState, useEffect } from 'react';
import { useApp } from '../App';
import api from '../api';
import { useI18n } from '../i18n/index.jsx';

const MONTH_NAMES = ['Январь','Февраль','Март','Апрель','Май','Июнь','Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
const CONTRACT_LABELS = {
  umowa_o_prace: 'Umowa o pracę',
  umowa_zlecenie: 'Umowa zlecenie',
  umowa_o_dzielo: 'Umowa o dzieło',
  kontrakt_menedzerski: 'Kontrakt',
  b2b: 'B2B',
};

function pctChange(curr, prev) {
  if (!prev) return curr > 0 ? '+100%' : '—';
  const pct = ((curr - prev) / prev * 100).toFixed(1);
  return pct > 0 ? `+${pct}%` : `${pct}%`;
}

function fmt(n) {
  return (n || 0).toLocaleString('pl-PL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function ZusDashboard() {
  const { setPage, currentCompany } = useApp();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  useEffect(() => { load(); }, [year, month, currentCompany?.id]);

  async function load() {
    setLoading(true);
    try {
      const cid = currentCompany?.id;
      const cidParam = cid ? `&company_id=${cid}` : '';
      const res = await api.get(`/hr/zus-dashboard?year=${year}&month=${month}${cidParam}`);
      setData(res.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  }

  function prevMonth() {
    if (month === 1) { setMonth(12); setYear(y => y - 1); }
    else setMonth(m => m - 1);
  }
  function nextMonth() {
    if (month === 12) { setMonth(1); setYear(y => y + 1); }
    else setMonth(m => m + 1);
  }

  const cardStyle = {
    padding: 16, borderRadius: 14,
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
    flex: 1, minWidth: 140,
  };
  const labelStyle = { fontSize: 11, opacity: 0.5, marginBottom: 4 };
  const valueStyle = { fontSize: 20, fontWeight: 700 };
  const changeUp = { fontSize: 11, color: '#f87171', marginTop: 2 };
  const changeDown = { fontSize: 11, color: '#34d399', marginTop: 2 };

  if (loading && !data) return <div style={{ padding: 20, textAlign: 'center', opacity: 0.5 }}>{t('common.loading')}</div>;
  if (!data) return <div style={{ padding: 20, textAlign: 'center', opacity: 0.5 }}>Нет данных</div>;

  const zusChange = data.total_zus - data.prev_zus;
  const pitChange = data.total_pit - data.prev_pit;
  const costChange = data.totals.total_cost - data.prev_totals.total_cost;

  // Chart: simple bar chart from history
  const maxZus = Math.max(...data.history.map(h => h.zus), 1);

  return (
    <div style={{ padding: '0 12px 80px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, paddingTop: 8 }}>
        <button onClick={() => setPage('dashboard_owner')} style={{
          background: 'none', border: 'none', color: 'inherit', fontSize: 20, cursor: 'pointer', padding: 4,
        }}>←</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>🏦 ZUS Dashboard</div>
          <div style={{ fontSize: 12, opacity: 0.5 }}>Обязательства по ZUS и налогам</div>
        </div>
      </div>

      {/* Month selector */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, marginBottom: 16,
        padding: '10px 16px', borderRadius: 12, background: 'rgba(255,255,255,0.03)',
      }}>
        <button onClick={prevMonth} style={{ background: 'none', border: 'none', color: 'inherit', fontSize: 18, cursor: 'pointer' }}>◀</button>
        <span style={{ fontSize: 16, fontWeight: 600 }}>{MONTH_NAMES[month - 1]} {year}</span>
        <button onClick={nextMonth} style={{ background: 'none', border: 'none', color: 'inherit', fontSize: 18, cursor: 'pointer' }}>▶</button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ ...cardStyle, borderLeft: '3px solid #3b82f6' }}>
          <div style={labelStyle}>ZUS (всего)</div>
          <div style={valueStyle}>{fmt(data.total_zus)}</div>
          <div style={zusChange >= 0 ? changeUp : changeDown}>
            {pctChange(data.total_zus, data.prev_zus)} к пред. мес.
          </div>
        </div>
        <div style={{ ...cardStyle, borderLeft: '3px solid #f59e0b' }}>
          <div style={labelStyle}>PIT (аванс)</div>
          <div style={valueStyle}>{fmt(data.total_pit)}</div>
          <div style={pitChange >= 0 ? changeUp : changeDown}>
            {pctChange(data.total_pit, data.prev_pit)}
          </div>
        </div>
        <div style={{ ...cardStyle, borderLeft: '3px solid #10b981' }}>
          <div style={labelStyle}>Общая стоимость</div>
          <div style={valueStyle}>{fmt(data.totals.total_cost)}</div>
          <div style={costChange >= 0 ? changeUp : changeDown}>
            {pctChange(data.totals.total_cost, data.prev_totals.total_cost)}
          </div>
        </div>
      </div>

      {/* Payment Calendar */}
      <div style={{
        marginBottom: 16, padding: 16, borderRadius: 14,
        background: 'linear-gradient(135deg, rgba(59,130,246,0.08) 0%, rgba(59,130,246,0.02) 100%)',
        border: '1px solid rgba(59,130,246,0.15)',
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>📅 Календарь платежей</div>
        <div style={{ fontSize: 11, opacity: 0.4, marginBottom: 10 }}>
          Сроки за {MONTH_NAMES[month - 1]} {year} (оплата в след. месяце)
        </div>
        {data.calendar.map((dl, i) => {
          const colors = {
            overdue: { bg: 'rgba(239,68,68,0.15)', border: '#ef4444', dot: '🔴' },
            upcoming: { bg: 'rgba(245,158,11,0.15)', border: '#f59e0b', dot: '🟡' },
            future: { bg: 'rgba(255,255,255,0.03)', border: 'rgba(255,255,255,0.1)', dot: '⚪' },
            paid: { bg: 'rgba(16,185,129,0.15)', border: '#10b981', dot: '🟢' },
          };
          const c = colors[dl.status] || colors.future;
          return (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
              marginBottom: 6, borderRadius: 10, background: c.bg,
              borderLeft: `3px solid ${c.border}`,
            }}>
              <span style={{ fontSize: 14 }}>{c.dot}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{dl.label}</div>
                <div style={{ fontSize: 11, opacity: 0.5 }}>{dl.date}</div>
              </div>
              <div style={{ fontSize: 14, fontWeight: 700 }}>
                {dl.amount > 0 ? `${fmt(dl.amount)} PLN` : '—'}
              </div>
            </div>
          );
        })}
      </div>

      {/* Employee breakdown */}
      <div style={{
        marginBottom: 16, borderRadius: 14, overflow: 'hidden',
        border: '1px solid rgba(255,255,255,0.08)',
      }}>
        <div style={{
          padding: '12px 16px', fontWeight: 700, fontSize: 15,
          background: 'rgba(255,255,255,0.04)',
        }}>
          👥 Разбивка по сотрудникам ({data.employees.length})
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
                {['Сотрудник','Тип','Брутто','ZUS раб.','ZUS работод.','Здров.','PIT','Нетто','Стоимость'].map(h => (
                  <th key={h} style={{ padding: '8px 6px', textAlign: 'left', fontWeight: 600, opacity: 0.6, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.employees.map((emp, i) => (
                <tr key={i} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <td style={{ padding: '8px 6px', fontWeight: 500, whiteSpace: 'nowrap' }}>{emp.name}</td>
                  <td style={{ padding: '8px 6px', opacity: 0.6, fontSize: 10 }}>{CONTRACT_LABELS[emp.contract_type] || emp.contract_type}</td>
                  <td style={{ padding: '8px 6px', fontVariantNumeric: 'tabular-nums' }}>{fmt(emp.gross)}</td>
                  <td style={{ padding: '8px 6px', fontVariantNumeric: 'tabular-nums' }}>{fmt(emp.zus_employee)}</td>
                  <td style={{ padding: '8px 6px', fontVariantNumeric: 'tabular-nums' }}>{fmt(emp.zus_employer)}</td>
                  <td style={{ padding: '8px 6px', fontVariantNumeric: 'tabular-nums' }}>{fmt(emp.zdrowotna)}</td>
                  <td style={{ padding: '8px 6px', fontVariantNumeric: 'tabular-nums' }}>{fmt(emp.pit)}</td>
                  <td style={{ padding: '8px 6px', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmt(emp.net)}</td>
                  <td style={{ padding: '8px 6px', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmt(emp.total_cost)}</td>
                </tr>
              ))}
              {/* Totals row */}
              <tr style={{ background: 'rgba(59,130,246,0.08)', fontWeight: 700 }}>
                <td style={{ padding: '10px 6px' }} colSpan={2}>ИТОГО</td>
                <td style={{ padding: '10px 6px' }}>{fmt(data.totals.gross)}</td>
                <td style={{ padding: '10px 6px' }}>{fmt(data.totals.zus_employee)}</td>
                <td style={{ padding: '10px 6px' }}>{fmt(data.totals.zus_employer)}</td>
                <td style={{ padding: '10px 6px' }}>{fmt(data.totals.zdrowotna)}</td>
                <td style={{ padding: '10px 6px' }}>{fmt(data.totals.pit)}</td>
                <td style={{ padding: '10px 6px' }}>{fmt(data.totals.net)}</td>
                <td style={{ padding: '10px 6px' }}>{fmt(data.totals.total_cost)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* 6-month ZUS trend chart */}
      {data.history && data.history.length > 0 && (
        <div style={{
          padding: 16, borderRadius: 14, marginBottom: 16,
          background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)',
        }}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>📊 Тренд ZUS (6 мес.)</div>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 120 }}>
            {data.history.map((h, i) => {
              const barH = maxZus > 0 ? (h.zus / maxZus * 100) : 0;
              const isCurrent = h.year === year && h.month === month;
              return (
                <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{ fontSize: 9, opacity: 0.6, fontVariantNumeric: 'tabular-nums' }}>
                    {h.zus > 0 ? fmt(h.zus) : ''}
                  </div>
                  <div style={{
                    width: '100%', maxWidth: 40, borderRadius: '6px 6px 0 0',
                    height: `${Math.max(barH, 2)}%`,
                    background: isCurrent
                      ? 'linear-gradient(180deg, #3b82f6, #1d4ed8)'
                      : 'linear-gradient(180deg, rgba(255,255,255,0.15), rgba(255,255,255,0.05))',
                    transition: 'height 0.3s',
                  }} />
                  <div style={{ fontSize: 9, opacity: 0.5 }}>
                    {MONTH_NAMES[h.month - 1]?.slice(0, 3)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Deadlines reference */}
      <div style={{
        padding: 14, borderRadius: 12, fontSize: 11, opacity: 0.5,
        background: 'rgba(255,255,255,0.02)', lineHeight: 1.8,
      }}>
        <div style={{ fontWeight: 600, marginBottom: 4, opacity: 0.8 }}>📋 Справка: сроки 2026</div>
        <div>5-е — ZUS владелец (JDG)</div>
        <div>15-е — ZUS DRA + RCA (до 20 сотр.) / PPK</div>
        <div>20-е — ZUS DRA (20+ сотр.) / PIT-4R / PIT-8AR</div>
        <div>25-е — VAT-7</div>
      </div>
    </div>
  );
}
